import type {
  AvailableTool,
  ProviderGroup,
} from '@cotera/client/app/hooks/use-available-tools';
import { Assert } from '@cotera/utilities';
import type { FilterMode, UseCase } from './constants';

/**
 * How much of a provider actually works, which is a different question from
 * `configStatus` ("is it connected at all"). A provider counts as connected the
 * moment one tool runs, so a row keyed off status alone shows a tick and
 * "50 tools" when only one of the fifty is usable.
 */
export type ProviderReadiness = 'none' | 'partial' | 'all';

export const providerReadiness = (
  provider: ProviderGroup
): ProviderReadiness => {
  const { configuredToolCount: ready, totalToolCount: total } = provider;
  if (total === 0 || ready === 0) {
    return 'none';
  }
  return ready >= total ? 'all' : 'partial';
};

/** Row subtitle: honest about how many of the provider's tools can run. */
export const providerToolSummary = (provider: ProviderGroup): string => {
  const { configuredToolCount: ready, totalToolCount: total } = provider;
  const noun = total === 1 ? 'tool' : 'tools';
  return providerReadiness(provider) === 'partial'
    ? `${ready} of ${total} ${noun} ready`
    : `${total} ${noun}`;
};

/**
 * What the provider list is matched against. Descriptions are indexed as well
 * as names: someone looking for a CRM searches "crm", which appears in the
 * blurb rather than in "HubSpot".
 */
export type ProviderSearchRecord = {
  provider: string;
  displayName: string;
  description: string;
};

export const toProviderSearchRecord = (
  provider: ProviderGroup
): ProviderSearchRecord => ({
  provider: provider.provider,
  displayName: provider.displayName,
  description: provider.description ?? '',
});

export const PROVIDER_SEARCH_KEYS: (keyof ProviderSearchRecord)[] = [
  'provider',
  'displayName',
  'description',
];

/**
 * What the tool list is matched against. Both description fields are indexed
 * because the row renders `defaultDescription ?? description`, so searching
 * only the latter would miss the text actually on screen.
 */
export type ToolSearchRecord = {
  key: string;
  name: string;
  description: string;
  defaultDescription: string;
  provider: string;
};

export const toToolSearchRecord = (tool: AvailableTool): ToolSearchRecord => ({
  key: tool.key,
  name: tool.name,
  description: tool.description,
  defaultDescription: tool.defaultDescription ?? '',
  provider: tool.extractedProvider,
});

export const TOOL_SEARCH_KEYS: (keyof ToolSearchRecord)[] = [
  'name',
  'description',
  'defaultDescription',
  'provider',
];

/**
 * Plain substring match for the per-provider tool filter, over every field of
 * the same record the fuzzy search indexes.
 *
 * Deliberately derived from `toToolSearchRecord` rather than listing fields
 * again: the two searches previously drifted, and the filter missed
 * `defaultDescription`, which is the copy the row actually renders. Filtering
 * by text on screen made the row vanish.
 */
export const toolMatchesFilter = (
  tool: AvailableTool,
  query: string
): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return true;
  }
  return Object.values(toToolSearchRecord(tool)).some((field) =>
    field.toLowerCase().includes(needle)
  );
};

/**
 * A single selectable line in the catalog list. Providers and individual tools
 * share one flat list so arrow-key navigation crosses group boundaries without
 * the caller tracking two indexes.
 */
export type CatalogRow =
  | { t: 'provider'; id: string; provider: ProviderGroup }
  | { t: 'tool'; id: string; tool: AvailableTool; provider: ProviderGroup };

export type CatalogGroup = {
  id: string;
  label: string;
  /** Rendered next to the label when the group was truncated. */
  hint?: string;
  rows: CatalogRow[];
};

export type Catalog = {
  groups: CatalogGroup[];
  /** Every row across every group, in visual order — the keyboard nav index space. */
  rows: CatalogRow[];
  /** Providers left after the use-case + connection filters, before search. */
  filteredProviderCount: number;
};

/**
 * Tool rows shown for a search. Ranked results past this point are dropped and
 * the count is surfaced in the group hint rather than silently truncated.
 */
export const MAX_TOOL_ROWS = 50;

/** Whether a provider belongs to `useCase`, by provider list or explicit tool key. */
const matchesUseCase = (
  provider: ProviderGroup,
  useCase: UseCase | null
): boolean => {
  if (useCase === null) {
    return true;
  }
  if (useCase.providers.includes(provider.provider)) {
    return true;
  }
  return (useCase.tools ?? []).some((toolKey) =>
    toolKey.startsWith(`${provider.provider}/`)
  );
};

const matchesFilter = (
  provider: ProviderGroup,
  filter: FilterMode
): boolean => {
  switch (filter) {
    case 'ALL':
      return true;
    case 'CONFIGURED':
      return provider.configStatus === 'configured';
    case 'SETUP':
      return provider.configStatus === 'unconfigured';
    default:
      return Assert.unreachable(filter);
  }
};

/** Rank map so fuzzy-search order survives the set intersections below. */
const rankOf = (order: string[]): Map<string, number> =>
  new Map(order.map((key, index) => [key, index]));

const providerRow = (provider: ProviderGroup): CatalogRow => ({
  t: 'provider',
  id: `provider:${provider.provider}`,
  provider,
});

const toolRow = (tool: AvailableTool, provider: ProviderGroup): CatalogRow => ({
  t: 'tool',
  id: `tool:${tool.key}`,
  tool,
  provider,
});

export type BuildCatalogArgs = {
  providers: ProviderGroup[];
  query: string;
  filter: FilterMode;
  useCase: UseCase | null;
  /** Provider keys in fuzzy-rank order. Ignored when `query` is empty. */
  providerMatchOrder: string[];
  /** Tool keys in fuzzy-rank order. Ignored when `query` is empty. */
  toolMatchOrder: string[];
  /**
   * Providers that own at least one curated-popular tool. Resolved by the
   * caller, because a tool key's first segment is not always its provider.
   */
  popularProviders: ReadonlySet<string>;
  maxToolRows?: number;
};

/**
 * Turns the provider catalog into the grouped list the browser renders.
 *
 * Browsing (no query) groups by connection state so the tools an org already
 * paid the setup cost for sit at the top. Searching switches to result groups —
 * matching tools first, because someone typing "send message" wants the action,
 * not a provider page they then have to scan.
 */
export const buildCatalog = ({
  providers,
  query,
  filter,
  useCase,
  providerMatchOrder,
  toolMatchOrder,
  popularProviders,
  maxToolRows = MAX_TOOL_ROWS,
}: BuildCatalogArgs): Catalog => {
  const candidates = providers.filter(
    (p) => matchesUseCase(p, useCase) && matchesFilter(p, filter)
  );

  const groups =
    query.length > 0
      ? searchGroups({
          candidates,
          providerMatchOrder,
          toolMatchOrder,
          maxToolRows,
        })
      : browseGroups({ candidates, filter, popularProviders });

  const nonEmpty = groups.filter((g) => g.rows.length > 0);

  return {
    groups: nonEmpty,
    rows: nonEmpty.flatMap((g) => g.rows),
    filteredProviderCount: candidates.length,
  };
};

const browseGroups = ({
  candidates,
  filter,
  popularProviders,
}: {
  candidates: ProviderGroup[];
  filter: FilterMode;
  popularProviders: ReadonlySet<string>;
}): CatalogGroup[] => {
  // Providers arrive alphabetically from `useAvailableTools`; a stable sort on
  // popularity alone keeps that as the tiebreak.
  const byPopularity = (a: ProviderGroup, b: ProviderGroup): number => {
    const aPopular = popularProviders.has(a.provider);
    const bPopular = popularProviders.has(b.provider);
    if (aPopular === bPopular) {
      return 0;
    }
    return aPopular ? -1 : 1;
  };

  const connected = candidates
    .filter((p) => p.configStatus === 'configured')
    .sort(byPopularity);
  const available = candidates
    .filter((p) => p.configStatus === 'unconfigured')
    .sort(byPopularity);

  // With a connection filter applied the second group is always empty, so the
  // remaining heading would just restate the filter chip.
  const showCounts = filter === 'ALL';

  return [
    {
      id: 'connected',
      label: 'Connected',
      hint: showCounts && connected.length > 0 ? `${connected.length}` : '',
      rows: connected.map(providerRow),
    },
    {
      id: 'available',
      label: 'Available',
      hint: showCounts && available.length > 0 ? `${available.length}` : '',
      rows: available.map(providerRow),
    },
  ];
};

const searchGroups = ({
  candidates,
  providerMatchOrder,
  toolMatchOrder,
  maxToolRows,
}: {
  candidates: ProviderGroup[];
  providerMatchOrder: string[];
  toolMatchOrder: string[];
  maxToolRows: number;
}): CatalogGroup[] => {
  const providerByKey = new Map(candidates.map((p) => [p.provider, p]));
  const toolRank = rankOf(toolMatchOrder);
  const providerRank = rankOf(providerMatchOrder);

  const matchedTools = candidates
    .flatMap((provider) =>
      provider.tools
        .filter((tool) => toolRank.has(tool.key))
        .map((tool) => ({ tool, provider }))
    )
    .sort(
      (a, b) =>
        (toolRank.get(a.tool.key) ?? 0) - (toolRank.get(b.tool.key) ?? 0)
    );

  const shownTools = matchedTools.slice(0, maxToolRows);

  // A provider is worth showing if its own name matched, or if it owns a
  // matching tool — searching "send message" should still offer the Slack page.
  const providersOwningMatches = new Set(
    matchedTools.map(({ provider }) => provider.provider)
  );
  const matchedProviders = candidates
    .filter(
      (p) =>
        providerRank.has(p.provider) || providersOwningMatches.has(p.provider)
    )
    .sort((a, b) => {
      const aRank = providerRank.get(a.provider);
      const bRank = providerRank.get(b.provider);
      if (aRank !== undefined && bRank !== undefined) {
        return aRank - bRank;
      }
      // Direct name matches outrank providers pulled in by a tool match.
      if (aRank !== undefined) {
        return -1;
      }
      if (bRank !== undefined) {
        return 1;
      }
      return a.displayName.localeCompare(b.displayName);
    });

  return [
    {
      id: 'tools',
      label: 'Tools',
      hint:
        matchedTools.length > shownTools.length
          ? `${shownTools.length} of ${matchedTools.length}`
          : `${matchedTools.length}`,
      rows: shownTools.map(({ tool, provider }) => toolRow(tool, provider)),
    },
    {
      id: 'providers',
      label: 'Providers',
      hint: `${matchedProviders.length}`,
      rows: matchedProviders
        .filter((p) => providerByKey.has(p.provider))
        .map(providerRow),
    },
  ];
};
